import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Network, Plus, Search, Edit2, Trash2,
  Users, Building2, ChevronRight, AlertCircle, FolderTree
} from '../../../components/icons';
import { useAuth } from '../../../contexts/AuthContext';
import { supabase } from '../../../lib/supabase';
import { getCoachIdForUser, loadCoachTeams } from '../../../lib/coach-teams';
import { LoadingSpinner } from '../../../components/LoadingSpinner';
import type { Team, TeamType } from '../../../types/team';

export function CoachTeamManagementTR() {
  const navigate = useNavigate();
  const { user, isLoading: authLoading } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [teams, setTeams] = useState<Team[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingTeam, setEditingTeam] = useState<Team | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [coachId, setCoachId] = useState<string | null>(null);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    type: 'team' as TeamType,
    parentId: '' as string,
  });

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate('/tr/login'); return; }

    const init = async () => {
      try {
        const cId = await getCoachIdForUser(user.id);
        if (!cId) { navigate('/tr/coach'); return; }
        setCoachId(cId);
        await refreshTeams(cId);
      } catch (err) {
        console.error('Error initializing:', err);
        setError('Takımlar yüklenemedi');
      } finally {
        setIsLoading(false);
      }
    };

    init();
  }, [user, authLoading, navigate]);

  const refreshTeams = async (cId: string) => {
    const loaded = await loadCoachTeams(cId);
    setTeams(loaded);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!coachId) return;

    try {
      if (editingTeam) {
        const { error: updateError } = await supabase
          .from('teams')
          .update({
            name: formData.name,
            description: formData.description || null,
            type: formData.type,
            parent_id: formData.parentId || null,
            updated_at: new Date().toISOString(),
          })
          .eq('id', editingTeam.id);

        if (updateError) throw updateError;
      } else {
        const { error: insertError } = await supabase
          .from('teams')
          .insert({
            coach_id: coachId,
            name: formData.name,
            description: formData.description || null,
            type: formData.type,
            parent_id: formData.parentId || null,
          });

        if (insertError) throw insertError;
      }

      await refreshTeams(coachId);
      closeModal();
    } catch (err: any) {
      console.error('Error saving team:', err);
      setError(err.message || 'Takim kaydedilemedi');
    }
  };

  const handleDelete = async (team: Team) => {
    if (!confirm(`"${team.name}" takimini silmek istediginizden emin misiniz? Bu islem tum uye iliskilerini kaldiacaktir.`)) return;
    if (!coachId) return;

    try {
      const { error: deleteError } = await supabase
        .from('teams')
        .delete()
        .eq('id', team.id);

      if (deleteError) throw deleteError;
      await refreshTeams(coachId);
    } catch (err: any) {
      setError(err.message || 'Takim silinemedi');
    }
  };

  const openEditModal = (team: Team) => {
    setEditingTeam(team);
    setFormData({
      name: team.name,
      description: team.description || '',
      type: team.type,
      parentId: team.parentId || '',
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingTeam(null);
    setFormData({ name: '', description: '', type: 'team', parentId: '' });
  };

  const departments = teams.filter(t => t.type === 'department');
  const standaloneTeams = teams.filter(t => t.type === 'team' && !t.parentId);
  const getChildTeams = (deptId: string) => teams.filter(t => t.type === 'team' && t.parentId === deptId);

  const filteredDepartments = departments.filter(d =>
    d.name.toLowerCase().includes(searchQuery.toLowerCase())
  );
  const filteredStandaloneTeams = standaloneTeams.filter(t =>
    t.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (authLoading || isLoading) {
    return <LoadingSpinner message="Yukleniyor..." />;
  }

  return (
    <div className="min-h-screen freud-bg">
      <header className="bg-white/80 backdrop-blur-sm border-b border-cream-300">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-warm-500 rounded-xl shadow-soft">
                <Network className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-chocolate-900">Takımlar</h1>
                <p className="text-sm text-chocolate-500">
                  {departments.length} grup{departments.length !== 1 ? '' : ''}, {teams.filter(t => t.type === 'team').length} takım
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => {
                  setFormData({ name: '', description: '', type: 'department', parentId: '' });
                  setEditingTeam(null);
                  setShowModal(true);
                }}
                className="freud-btn-secondary flex items-center gap-2"
              >
                <Building2 className="w-4 h-4" />
                Grup Ekle
              </button>
              <button
                onClick={() => {
                  setFormData({ name: '', description: '', type: 'team', parentId: '' });
                  setEditingTeam(null);
                  setShowModal(true);
                }}
                className="freud-btn-primary flex items-center gap-2"
              >
                <Plus className="w-5 h-5" />
                Takım Ekle
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        {error && (
          <div className="bg-coral-100 border border-coral-200 text-coral-700 px-4 py-3 rounded-xl mb-6 flex items-center gap-2">
            <AlertCircle className="w-5 h-5" />
            {error}
          </div>
        )}

        <div className="freud-card-elevated mb-6">
          <div className="p-4 border-b border-cream-200">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-chocolate-400" />
              <input
                type="text"
                placeholder="Takım veya takım ara..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="freud-input pl-10"
              />
            </div>
          </div>

          {teams.length === 0 ? (
            <div className="text-center py-16">
              <FolderTree className="w-16 h-16 text-cream-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-chocolate-700 mb-2">Henüz takım yok</h3>
              <p className="text-chocolate-500 mb-6">
                Danışanlarınızı organize etmek ve grup performansını takip etmek için takımlar oluşturun.
              </p>
              <button
                onClick={() => {
                  setFormData({ name: '', description: '', type: 'team', parentId: '' });
                  setEditingTeam(null);
                  setShowModal(true);
                }}
                className="freud-btn-primary inline-flex items-center gap-2"
              >
                <Plus className="w-5 h-5" />
                İlk Takımınızı Oluşturun
              </button>
            </div>
          ) : (
            <div className="divide-y divide-cream-200">
              {filteredDepartments.map((dept) => (
                <div key={dept.id}>
                  <TeamRowTR
                    team={dept}
                    onEdit={openEditModal}
                    onDelete={handleDelete}
                    onView={() => navigate(`/tr/coach/teams/${dept.id}`)}
                  />
                  {getChildTeams(dept.id).map(childTeam => (
                    <div key={childTeam.id} className="pl-8">
                      <TeamRowTR
                        team={childTeam}
                        onEdit={openEditModal}
                        onDelete={handleDelete}
                        onView={() => navigate(`/tr/coach/teams/${childTeam.id}`)}
                        isChild
                      />
                    </div>
                  ))}
                </div>
              ))}

              {filteredStandaloneTeams.map(team => (
                <TeamRowTR
                  key={team.id}
                  team={team}
                  onEdit={openEditModal}
                  onDelete={handleDelete}
                  onView={() => navigate(`/tr/coach/teams/${team.id}`)}
                />
              ))}

              {filteredDepartments.length === 0 && filteredStandaloneTeams.length === 0 && (
                <div className="text-center py-12 text-chocolate-400">
                  Aramanızla eşleşen takım veya grup bulunamadı
                </div>
              )}
            </div>
          )}
        </div>
      </main>

      {showModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="freud-card-elevated p-6 w-full max-w-md m-4"
          >
            <h2 className="text-xl font-bold text-chocolate-900 mb-6">
              {editingTeam ? `${formData.type === 'department' ? 'Grubu' : 'Takimi'} Duzenle` : `${formData.type === 'department' ? 'Grup' : 'Takim'} Olustur`}
            </h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-chocolate-700 mb-1">Takım Adı</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  placeholder={formData.type === 'department' ? 'örn. Liderlik Takımı' : 'örn. Q1 Grubu'}
                  className="freud-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-chocolate-700 mb-1">Açıklama (isteğe bağlı)</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Kısa açıklama..."
                  rows={3}
                  className="freud-input resize-none"
                />
              </div>
              {!editingTeam && (
                <div>
                  <label className="block text-sm font-medium text-chocolate-700 mb-1">Tur</label>
                  <select
                    value={formData.type}
                    onChange={(e) => setFormData({ ...formData, type: e.target.value as TeamType, parentId: '' })}
                    className="freud-input"
                  >
                    <option value="team">Takim</option>
                    <option value="department">Grup</option>
                  </select>
                </div>
              )}
              {formData.type === 'team' && departments.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-chocolate-700 mb-1">Üst Grup (isteğe bağlı)</label>
                  <select
                    value={formData.parentId}
                    onChange={(e) => setFormData({ ...formData, parentId: e.target.value })}
                    className="freud-input"
                  >
                    <option value="">Grup yok (bağımsız)</option>
                    {departments
                      .filter(d => d.id !== editingTeam?.id)
                      .map(d => (
                        <option key={d.id} value={d.id}>{d.name}</option>
                      ))
                    }
                  </select>
                </div>
              )}
              <div className="flex gap-3 pt-4">
                <button type="button" onClick={closeModal} className="freud-btn-secondary flex-1">
                  İptal
                </button>
                <button type="submit" className="freud-btn-primary flex-1">
                  {editingTeam ? 'Değişiklikleri Kaydet' : `${formData.type === 'department' ? 'Grup' : 'Takim'} Olustur`}
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}
    </div>
  );
}

function TeamRowTR({
  team,
  onEdit,
  onDelete,
  onView,
  isChild = false,
}: {
  team: Team;
  onEdit: (t: Team) => void;
  onDelete: (t: Team) => void;
  onView: () => void;
  isChild?: boolean;
}) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className={`flex items-center justify-between px-6 py-4 hover:bg-cream-50 transition-colors cursor-pointer group ${isChild ? 'border-l-2 border-warm-200' : ''}`}
      onClick={onView}
    >
      <div className="flex items-center gap-4 flex-1 min-w-0">
        <div className={`p-2 rounded-lg ${team.type === 'department' ? 'bg-warm-100' : 'bg-sage-100'}`}>
          {team.type === 'department'
            ? <Building2 className="w-5 h-5 text-warm-600" />
            : <Users className="w-5 h-5 text-sage-600" />
          }
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="font-medium text-chocolate-900 truncate">{team.name}</span>
            <span className={`px-2 py-0.5 rounded-full text-xs capitalize ${
              team.type === 'department' ? 'bg-warm-100 text-warm-700' : 'bg-sage-100 text-sage-700'
            }`}>
              {team.type === 'department' ? 'grup' : 'takim'}
            </span>
          </div>
          {team.description && (
            <p className="text-sm text-chocolate-500 truncate">{team.description}</p>
          )}
        </div>
      </div>

      <div className="flex items-center gap-4">
        <div className="flex items-center gap-1.5 text-sm text-chocolate-500">
          <Users className="w-4 h-4" />
          <span>{team.memberCount || 0}</span>
        </div>
        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <button
            onClick={(e) => { e.stopPropagation(); onEdit(team); }}
            className="p-2 text-chocolate-600 hover:bg-cream-200 rounded-lg transition-colors"
          >
            <Edit2 className="w-4 h-4" />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onDelete(team); }}
            className="p-2 text-coral-600 hover:bg-coral-50 rounded-lg transition-colors"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
        <ChevronRight className="w-5 h-5 text-chocolate-300 group-hover:text-chocolate-500 transition-colors" />
      </div>
    </motion.div>
  );
}
